import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useApproveBooking,
  useRejectBooking,
  useCancelBooking,
  useCheckInBooking,
  useCompleteBooking,
  useNoShowBooking,
  useUpdateBooking,
  useBookingHistory,
} from '@hooks/useBookings';
import { adminCentersApi } from '@api/client';
import { useConfirm } from '@hooks/useConfirm';
import type { Booking, BookingStatus, Center } from '@api/types';
import * as Icons from '@components/Icons';

export function fmtTime(iso: string) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso.slice(11, 16);
  }
}

export const STATUS_MAP: Record<BookingStatus, { label: string; tone: string }> = {
  pending:                   { label: 'Pendiente',          tone: 'warn'    },
  confirmed:                 { label: 'Confirmada',         tone: 'ok'      },
  checked_in:                { label: 'En sala',            tone: 'ok'      },
  cancelled:                 { label: 'Cancelada',          tone: 'danger'  },
  rescheduled:               { label: 'Reprogramada',       tone: 'info'    },
  completed:                 { label: 'Completada',         tone: 'neutral' },
  noshow:                    { label: 'No presentado',      tone: 'neutral' },
  pending_clinic_approval:   { label: 'Pend. aprobación',  tone: 'warn'    },
  rejected:                  { label: 'Rechazada',          tone: 'danger'  },
};

export function StatusPill({ status }: { status: BookingStatus }) {
  const s = STATUS_MAP[status];
  return <span className="pill" data-tone={s.tone}>{s.label}</span>;
}

export function initials(name: string) {
  return name.split(' ').map((p) => p.charAt(0)).join('').slice(0, 2).toUpperCase();
}

export function tone(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return Math.abs(h) % 5;
}

const ACTION_LABELS: Record<string, string> = {
  'booking.created':                 'Reserva creada',
  'booking.confirmed':               'Reserva confirmada',
  'booking.cancelled':               'Reserva cancelada',
  'booking.completed':               'Reserva completada',
  'booking.noshow':                  'No presentado',
  'booking.pending_clinic_approval': 'Enviada a revisión',
  'booking.rejected':                'Reserva rechazada',
  'booking.updated':                 'Reserva actualizada',
  'booking.rescheduled':             'Reserva reprogramada',
};

const STATUS_LABELS: Record<string, string> = {
  pending:                  'Pendiente',
  confirmed:                'Confirmada',
  checked_in:               'En sala',
  cancelled:                'Cancelada',
  completed:                'Completada',
  noshow:                   'No presentado',
  pending_clinic_approval:  'Pend. aprobación',
  rejected:                 'Rechazada',
  rescheduled:              'Reprogramada',
};

function fmtIso(iso: unknown): string {
  if (typeof iso !== 'string') return '—';
  try {
    return new Date(iso).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function AuditDiff({ before, after }: { before: Record<string, unknown> | null; after: Record<string, unknown> | null }) {
  if (!before && !after) return null;
  const lines: { key: string; from: string; to: string }[] = [];

  const bStatus = typeof before?.status === 'string' ? before.status : null;
  const aStatus = typeof after?.status  === 'string' ? after.status  : null;
  if (bStatus !== aStatus && (bStatus ?? aStatus)) {
    lines.push({
      key: 'Estado',
      from: bStatus ? (STATUS_LABELS[bStatus] ?? bStatus) : '—',
      to:   aStatus ? (STATUS_LABELS[aStatus] ?? aStatus) : '—',
    });
  }
  if (before?.startIso !== after?.startIso && (before?.startIso ?? after?.startIso)) {
    lines.push({
      key: 'Fecha',
      from: fmtIso(before?.startIso),
      to:   fmtIso(after?.startIso),
    });
  }
  if (lines.length === 0) return null;
  return (
    <div style={{ marginTop: 2 }}>
      {lines.map((l) => (
        <div key={l.key} style={{ fontSize: 11, color: 'var(--fg-muted)', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 500 }}>{l.key}:</span>
          {l.from !== '—' && <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>{l.from}</span>}
          {l.from !== '—' && <span>→</span>}
          <span style={{ color: 'var(--fg)' }}>{l.to}</span>
        </div>
      ))}
    </div>
  );
}

export function BookingDetailDrawer({
  booking,
  onClose,
}: {
  booking: Booking;
  onClose: () => void;
}) {
  const approveMutation = useApproveBooking();
  const rejectMutation = useRejectBooking();
  const cancelMutation = useCancelBooking();
  const checkInMutation = useCheckInBooking();
  const completeMutation = useCompleteBooking();
  const noShowMutation = useNoShowBooking();
  const { data: history, isLoading: historyLoading } = useBookingHistory(booking.id);
  const { data: center } = useQuery<Center>({
    queryKey: ['center', 'me'],
    queryFn: () => adminCentersApi.getMe(),
    staleTime: 300_000,
  });
  const checkInEnabled = center?.checkInEnabled ?? false;
  const updateMut = useUpdateBooking();
  const [staffNoteDraft, setStaffNoteDraft] = useState(booking.staffNote ?? '');
  const [staffNoteEditing, setStaffNoteEditing] = useState(false);
  const [staffNoteError, setStaffNoteError] = useState(false);
  const confirm = useConfirm();

  async function handleCancelClick() {
    const ok = await confirm({
      title: 'Cancelar reserva',
      message: (
        <>
          Vas a cancelar la cita de <strong>{booking.customerName}</strong> del{' '}
          <strong>{booking.startTime.slice(0, 10)} a las {fmtTime(booking.startTime)}</strong>.
          Esta acción no se puede deshacer y enviará un email al cliente.
        </>
      ),
      tone: 'danger',
      confirmLabel: 'Sí, cancelar',
      cancelLabel: 'Volver',
    });
    if (ok) {
      cancelMutation.mutate({ id: booking.id, reason: 'Cancelada por staff' });
      onClose();
    }
  }

  const duration = useMemo(() => {
    try {
      const start = new Date(booking.startTime);
      const end = new Date(booking.endTime);
      return Math.round((end.getTime() - start.getTime()) / 60000);
    } catch {
      return 0;
    }
  }, [booking.startTime, booking.endTime]);

  const isConfirmed       = booking.status === 'confirmed';
  const isCheckedIn       = booking.status === 'checked_in';
  const isPending         = booking.status === 'pending';
  const isPendingApproval = booking.status === 'pending_clinic_approval';
  const isFinal           = ['completed', 'cancelled', 'noshow', 'rejected'].includes(booking.status);

  return (
    <div className="modal-scrim" data-variant="drawer" onClick={onClose}>
      <div className="modal" onClick={(e) => { e.stopPropagation(); }} style={{ width: 'min(560px, 100%)' }}>
        <div className="bd-head">
          <div className="bd-ref">
            <span className="mono">{booking.id.slice(0, 12).toUpperCase()}</span>
            <span>·</span>
            <StatusPill status={booking.status} />
            <button
              className="icon-btn"
              data-variant="ghost"
              style={{ marginLeft: 'auto' }}
              onClick={onClose}
            >
              <Icons.X size={14} />
            </button>
          </div>
          <h2 className="bd-title">{booking.serviceName}</h2>
          <div className="bd-meta">
            <span className="mono">{booking.startTime.slice(0, 10)} · {fmtTime(booking.startTime)}</span>
            {' · '}{duration} min
          </div>
        </div>

        <div className="modal-body" style={{ gap: 18 }}>
          {/* Quick actions */}
          {(isConfirmed || isCheckedIn || isPending || isPendingApproval) && !isFinal && (
            <div style={{ display: 'flex', gap: 6 }}>
              {(isPending || isPendingApproval) && (
                <button
                  className="btn"
                  data-variant="primary"
                  style={{ flex: 1 }}
                  onClick={() => { approveMutation.mutate(booking.id); onClose(); }}
                  disabled={approveMutation.isPending}
                >
                  <Icons.Check size={13} /> Aprobar
                </button>
              )}
              {isPendingApproval && (
                <button
                  className="btn"
                  data-variant="danger"
                  style={{ flex: 1 }}
                  onClick={() => { rejectMutation.mutate(booking.id); onClose(); }}
                  disabled={rejectMutation.isPending}
                >
                  <Icons.X size={13} /> Rechazar
                </button>
              )}
              {isConfirmed && checkInEnabled && (
                <button
                  className="btn"
                  data-variant="primary"
                  style={{ flex: 1 }}
                  onClick={() => { checkInMutation.mutate(booking.id); }}
                  disabled={checkInMutation.isPending}
                >
                  <Icons.Check size={13} /> Check-in
                </button>
              )}
              {(isConfirmed || isCheckedIn) && (
                <button
                  className="btn"
                  style={{ flex: 1 }}
                  onClick={() => { completeMutation.mutate(booking.id); }}
                  disabled={completeMutation.isPending}
                >
                  <Icons.Check size={13} /> Completada
                </button>
              )}
              {(isConfirmed || isCheckedIn) && (
                <button
                  className="btn"
                  style={{ flex: 1 }}
                  onClick={() => { noShowMutation.mutate(booking.id); onClose(); }}
                  disabled={noShowMutation.isPending}
                >
                  No presentado
                </button>
              )}
              <button
                className="btn"
                data-variant="danger"
                style={{ flex: 1 }}
                onClick={() => { void handleCancelClick(); }}
                disabled={cancelMutation.isPending}
              >
                <Icons.X size={13} /> Cancelar
              </button>
            </div>
          )}

          {/* Client card */}
          <div className="card">
            <div className="card-head">
              <span className="card-title">Cliente</span>
            </div>
            <div style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                className="avatar"
                data-tone={tone(booking.customerName)}
                style={{ width: 44, height: 44, fontSize: 14 }}
              >
                {initials(booking.customerName)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: 14.5 }}>{booking.customerName}</div>
                <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
                  {booking.customerEmail} · <span className="mono">{booking.customerPhone}</span>
                </div>
              </div>
              <a href={`mailto:${booking.customerEmail}`} className="btn" data-size="sm">
                <Icons.Mail size={13} /> Email
              </a>
            </div>
            {booking.customerInternalNote && (
              <div
                style={{
                  margin: '0 16px 14px',
                  padding: '10px 12px',
                  background: 'var(--bg-soft)',
                  borderLeft: '3px solid var(--accent)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  color: 'var(--fg)',
                  whiteSpace: 'pre-wrap',
                }}
                title="Nota interna sobre este cliente. Editable en Clientes."
              >
                <div style={{ fontSize: 10.5, color: 'var(--fg-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                  Nota sobre el cliente
                </div>
                {booking.customerInternalNote}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="card">
            <div className="card-head"><span className="card-title">Detalles</span></div>
            <div className="kv-list" style={{ padding: '0 18px 14px' }}>
              <div className="kv-row">
                <span className="kv-k">Servicio</span>
                <span className="kv-v">{booking.serviceName} · <span className="mono">{duration} min</span></span>
              </div>
              <div className="kv-row">
                <span className="kv-k">Profesional</span>
                <span className="kv-v">{booking.staffName ?? '—'}</span>
              </div>
              <div className="kv-row">
                <span className="kv-k">Sala</span>
                <span className="kv-v">{booking.roomName ?? '—'}</span>
              </div>
              <div className="kv-row">
                <span className="kv-k">Inicio</span>
                <span className="kv-v mono">{booking.startTime.slice(0, 10)} · {fmtTime(booking.startTime)}</span>
              </div>
              <div className="kv-row">
                <span className="kv-k">Token</span>
                <span className="kv-v mono" style={{ fontSize: 11.5, color: 'var(--fg-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {booking.trackingToken}
                  <button
                    className="icon-btn"
                    data-variant="ghost"
                    style={{ width: 22, height: 22 }}
                    onClick={() => { void navigator.clipboard.writeText(booking.trackingToken); }}
                    title="Copiar"
                  >
                    <Icons.Code size={11} />
                  </button>
                </span>
              </div>
              <div className="kv-row">
                <span className="kv-k">Nota del cliente</span>
                <span className="kv-v" style={booking.customerNote ? {} : { color: 'var(--fg-soft)', fontStyle: 'italic' }}>
                  {booking.customerNote ?? 'Sin nota'}
                </span>
              </div>
              <div className="kv-row">
                <span className="kv-k">Nota del centro</span>
                <span className="kv-v">
                  {staffNoteEditing ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <textarea
                        className="textarea"
                        value={staffNoteDraft}
                        onChange={(e) => { setStaffNoteDraft(e.target.value); }}
                        style={{ minHeight: 60, fontSize: 13 }}
                        autoFocus
                      />
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          className="btn"
                          data-size="sm"
                          data-variant="primary"
                          disabled={updateMut.isPending}
                          onClick={() => {
                            setStaffNoteError(false);
                            const trimmed = staffNoteDraft.trim();
                            updateMut.mutate(
                              { id: booking.id, data: { staffNote: trimmed.length > 0 ? trimmed : null } },
                              {
                                onSuccess: () => { setStaffNoteEditing(false); },
                                onError: () => { setStaffNoteError(true); },
                              },
                            );
                          }}
                        >
                          {updateMut.isPending ? 'Guardando…' : 'Guardar'}
                        </button>
                        <button
                          className="btn"
                          data-size="sm"
                          onClick={() => { setStaffNoteDraft(booking.staffNote ?? ''); setStaffNoteEditing(false); setStaffNoteError(false); }}
                        >
                          Cancelar
                        </button>
                      </div>
                      {staffNoteError && (
                        <span style={{ fontSize: 11.5, color: 'var(--danger)' }}>
                          No se pudo guardar la nota. Inténtalo de nuevo.
                        </span>
                      )}
                    </div>
                  ) : (
                    <span style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                      <span style={booking.staffNote ? {} : { color: 'var(--fg-soft)', fontStyle: 'italic' }}>
                        {booking.staffNote ?? 'Sin nota'}
                      </span>
                      <button className="btn" data-variant="ghost" data-size="sm" onClick={() => { setStaffNoteEditing(true); }}>
                        <Icons.Edit size={11} /> Editar
                      </button>
                    </span>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* History timeline */}
        <div style={{ padding: '0 20px 16px' }}>
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
            <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--fg-muted)', marginBottom: 10 }}>
              Historial
            </p>
            {historyLoading && (
              <p style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Cargando…</p>
            )}
            {!historyLoading && (!history || history.length === 0) && (
              <p style={{ fontSize: 12, color: 'var(--fg-muted)' }}>Sin eventos registrados.</p>
            )}
            {history && history.length > 0 && (
              <ol style={{ listStyle: 'none', margin: 0, padding: 0, borderLeft: '1px solid var(--border)', marginLeft: 6 }}>
                {history.map((entry) => (
                  <li key={entry.id} style={{ paddingLeft: 14, paddingBottom: 10, position: 'relative' }}>
                    <span style={{
                      position: 'absolute', left: -5, top: 3,
                      width: 9, height: 9, borderRadius: '50%',
                      background: 'var(--fg-muted)', border: '2px solid var(--bg)',
                      display: 'block',
                    }} />
                    <p style={{ fontSize: 12, fontWeight: 500, margin: 0 }}>{ACTION_LABELS[entry.action] ?? entry.action}</p>
                    <p style={{ fontSize: 11, color: 'var(--fg-muted)', margin: 0 }}>
                      {new Date(entry.createdAt).toLocaleString('es-ES', {
                        day: '2-digit', month: '2-digit', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </p>
                    <AuditDiff before={entry.before} after={entry.after} />
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        <div className="modal-foot">
          <button className="btn" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}
